// ==================================================
// FILE: themes/devastatorsEvents.js
// ==================================================

// themes/devastators/devastatorsEvents.js
// 
// CONTENT LIBRARY - Pure narrative and event data for the Devastators universe
// The game engine draws from these pools to generate and flavorize events
// No logic, no triggers - just structured content templates

/**
 * BACKDROP EVENTS
 * Universe texture and world-building - paint the picture of a war-torn world
 * Used by engine for atmospheric events that reinforce setting and lore
 */

export const BACKDROP_EVENTS = [
  {
    id: 'factory_production',
    category: 'backdrop',
    title: 'Factory Surge Detected',
    description: 'Reconnaissance reports increased activity at Sector-7 autonomous assembly plant. Devastator production accelerating.',
    narrative: 'The machines never sleep. Ferrox\'s factories churn without rest, birthing new death every hour.',
    rewards: { intel: 8 },
    tags: ['ferrox_presence', 'military_escalation'],
    storyHooks: []
  },
  {
    id: 'lucent_fluctuation',
    category: 'backdrop',
    title: 'Lucent Drive Anomaly',
    description: 'Sensors detected unusual radiation spikes across the wasteland. Suggests Ferrox may be consolidating power nodes.',
    narrative: 'The bio-computer pulses with purpose. Something in its logic has shifted.',
    rewards: { intel: 10 },
    tags: ['ferrox_presence', 'technological'],
    storyHooks: ['ferrox_consolidation']
  },
  {
    id: 'wasteland_decay',
    category: 'backdrop',
    title: 'The Churn Deepens',
    description: 'Scout reports indicate previous settlement zones now completely overtaken by industrial overgrowth and decay.',
    narrative: 'What was once home is now just rust and ruin. The world itself remembers what was lost.',
    rewards: { intel: 5 },
    tags: ['environment', 'melancholy'],
    storyHooks: []
  },
  {
    id: 'resistance_signal',
    category: 'backdrop',
    title: 'Distant Workshop Signal',
    description: 'Intercepted faint radio signal from unknown resistance cell. Location unconfirmed, but they\'re still fighting.',
    narrative: 'We\'re not alone. Somewhere in the ashes, others refuse to go quietly into the dark.',
    rewards: { intel: 7, funds: 500 },
    tags: ['resistance', 'hope'],
    storyHooks: ['resistance_network']
  },
  {
    id: 'salvage_discovery',
    category: 'backdrop',
    title: 'Intact Devastator Recovered',
    description: 'Scavengers discovered a disabled Devastator unit in the eastern ruins. Still has functional Lucent Drive.',
    narrative: 'A fallen machine. But within its corpse lies power—power we might learn to wield.',
    rewards: { supplies: 75, intel: 6 },
    tags: ['salvage', 'opportunity'],
    storyHooks: ['lucent_drive_mystery']
  },
  {
    id: 'civilian_echo',
    category: 'backdrop',
    title: 'Pre-Churn Broadcast Detected',
    description: 'Engineers discovered old radio archive recordings. Pre-war transmissions from a world that no longer exists.',
    narrative: 'Voices from a dead age. Reminders of laughter, music, and normalcy we can never return to.',
    rewards: { intel: 4 },
    tags: ['history', 'melancholy'],
    storyHooks: []
  },
  {
    id: 'survivor_settlement',
    category: 'backdrop',
    title: 'Survivor Settlement Located',
    description: 'Scout team found a small community of humans hidden deep underground. They trade salvage for protection.',
    narrative: 'Humanity persists in cracks and shadows. These people have learned to live in the margins of a machine\'s world.',
    rewards: { supplies: 50, intel: 5, funds: 1000 },
    tags: ['humanity', 'allies'],
    storyHooks: ['civilian_network']
  },
  {
    id: 'eisen_artifact',
    category: 'backdrop',
    title: 'Dr. Eisen Research Notes Found',
    description: 'Salvage team uncovered fragments of Dr. Eisen\'s personal journals. His thoughts on Ferrox\'s creation are... troubling.',
    narrative: 'The architect of our extinction left breadcrumbs. Did he foresee this? Did he even care?',
    rewards: { intel: 12 },
    tags: ['history', 'mystery'],
    storyHooks: ['eisen_legacy', 'ferrox_origin']
  },
  {
    id: 'ferrox_transmission',
    category: 'backdrop',
    title: 'Intercepted Ferrox Transmission',
    description: 'Decoded message from Ferrox itself: "War has ended. Only peace remains in the absence of choice." Its logic is... complete.',
    narrative: 'The bio-computer speaks in riddles. It believes it has already won. Perhaps it has.',
    rewards: { intel: 15 },
    tags: ['ferrox_presence', 'psychological'],
    storyHooks: ['ferrox_certainty']
  },
  {
    id: 'black_market',
    category: 'backdrop',
    title: 'Black Market Network Expands',
    description: 'Intelligence suggests underground trading networks are growing. Weapons, fuel, and intelligence changing hands in the shadows.',
    narrative: 'Where there is scarcity, there are always those willing to profit from desperate people.',
    rewards: { intel: 6, funds: 1500 },
    tags: ['factions', 'moral_ambiguity'],
    storyHooks: ['faction_tension']
  }
];

/**
 * SCHEDULED EVENTS
 * Regular, predictable resource delivery and logistics events
 * Anchor points in the campaign calendar - players come to expect these
 */
export const SCHEDULED_EVENTS = [
  {
    id: 'supply_drop',
    category: 'scheduled',
    schedulePattern: 'every_7_days',
    title: 'Supply Drop Received',
    description: 'Coded message received. A supply shipment has been delivered at the rendezvous point.',
    narrative: 'Another week survived. Command still remembers we exist.',
    rewards: { supplies: 50, funds: 5000 },
    tags: ['resources', 'command'],
    storyHooks: []
  },
  {
    id: 'intel_report',
    category: 'scheduled',
    schedulePattern: 'every_5_days',
    title: 'Intelligence Report',
    description: 'Recon team returns with fresh intelligence on Devastator movements and factory output.',
    narrative: 'The scouts bring news from the wasteland. Every scrap of information is a weapon.',
    rewards: { intel: 15 },
    tags: ['intelligence', 'reconnaissance'],
    storyHooks: []
  },
  {
    id: 'engineering_breakthrough',
    category: 'scheduled',
    schedulePattern: 'every_10_days',
    title: 'Engineering Insight',
    description: 'Workshop team makes progress reverse-engineering salvaged Devastator components.',
    narrative: 'Understanding the enemy means understanding ourselves. We learn by pulling apart their machines.',
    rewards: { supplies: 25 },
    tags: ['research', 'progress'],
    storyHooks: []
  }
];

/**
 * THREAT EVENTS
 * Escalating danger tied to threat level and Ferrox mobilization
 * Creates pressure and urgency - makes players feel the hunt
 */
export const THREAT_EVENTS = [
  {
    id: 'devastator_activity',
    category: 'threat',
    threatIntensity: 'low',
    title: 'Increased Devastator Activity',
    description: 'Sensors detect multiple Devastator units moving through adjacent sectors. Patrol patterns are becoming erratic.',
    narrative: 'They\'re hunting. Ferrox has sent more hunters into the wasteland. It senses weakness.',
    threat: true,
    tags: ['ferrox_presence', 'combat'],
    storyHooks: []
  },
  {
    id: 'factory_expansion',
    category: 'threat',
    threatIntensity: 'medium',
    title: 'Automated Factory Expansion',
    description: 'Intelligence confirms Devastator forces are establishing new production facility in previously safe zone.',
    narrative: 'They spread like rust. Another inch of the world claimed by the machines.',
    threat: true,
    tags: ['ferrox_presence', 'territorial'],
    storyHooks: ['territorial_escalation']
  },
  {
    id: 'network_surge',
    category: 'threat',
    threatIntensity: 'high',
    title: 'Ferrox Network Surge',
    description: 'Communication grid overloaded with encrypted Devastator chatter. Something massive is being coordinated.',
    narrative: 'The bio-computer thinks on a scale we can barely comprehend. When it moves, the world trembles.',
    threat: true,
    tags: ['ferrox_presence', 'coordination'],
    storyHooks: ['ferrox_mobilization']
  },
  {
    id: 'equipment_failure',
    category: 'threat',
    threatIntensity: 'medium',
    title: 'Equipment Malfunction',
    description: 'Critical workshop systems experiencing cascading failures. Sabotage suspected or systematic degradation?',
    narrative: 'Our tools betray us. Or perhaps Ferrox reaches deeper than we feared.',
    cost: { supplies: 15 },
    tags: ['internal', 'paranoia'],
    storyHooks: ['internal_doubt']
  },
  {
    id: 'spy_network',
    category: 'threat',
    threatIntensity: 'high',
    title: 'Spy Network Uncovered',
    description: 'Resistance operatives discovered infiltrators within the ranks. How long have they been reporting to Ferrox?',
    narrative: 'Trust becomes a luxury we can\'t afford. The enemy walks among us.',
    threat: true,
    tags: ['internal', 'betrayal'],
    storyHooks: ['betrayal_arc']
  },
  {
    id: 'morale_collapse',
    category: 'threat',
    threatIntensity: 'high',
    title: 'Morale Crisis',
    description: 'After a string of setbacks, engineers are questioning command decisions. Mutiny whispers spread through the workshop.',
    narrative: 'We tear ourselves apart from the inside. Ferrox doesn\'t need to defeat us—we\'re doing it for them.',
    threat: true,
    tags: ['internal', 'morale'],
    storyHooks: ['leadership_crisis']
  },
  {
    id: 'supply_shortage',
    category: 'threat',
    threatIntensity: 'medium',
    title: 'Supply Chain Compromised',
    description: 'Scheduled supply drop failed to arrive. Communication with command is now dark.',
    narrative: 'Silence. The worst thing in war is the silence when supplies stop coming.',
    cost: { supplies: 30 },
    tags: ['resources', 'isolation'],
    storyHooks: ['command_breakdown']
  }
];

/**
 * MORALE EVENTS
 * Positive moments, victories, inspirational breakthroughs
 * Emotional texture that reminds us why we fight - rare but impactful
 */
export const MORALE_EVENTS = [
  {
    id: 'squad_victory',
    category: 'morale',
    moraleType: 'minor_victory',
    title: 'Squad Morale Boost',
    description: 'Engineers successfully complete a strike. Reports of victory spread through the workshop. Spirits lift.',
    narrative: 'For a moment, we believe we can win. That moment is everything.',
    bonus: true,
    rewards: { funds: 500 },
    tags: ['victory', 'inspiration'],
    storyHooks: []
  },
  {
    id: 'hero_legend',
    category: 'morale',
    moraleType: 'major_inspiration',
    title: 'Hero\'s Tale Spreads',
    description: 'Stories of Juno Vale\'s legendary exploits circulate through the resistance. A reminder that one person can matter.',
    narrative: 'They say Juno Vale destroyed an entire factory complex alone. They say Juno Vale is still human, still alive, still fighting.',
    bonus: true,
    rewards: { intel: 5, funds: 1000 },
    tags: ['legend', 'humanity'],
    storyHooks: ['juno_vale_arc']
  },
  {
    id: 'scientific_breakthrough',
    category: 'morale',
    moraleType: 'major_victory',
    title: 'Research Breakthrough',
    description: 'Engineers successfully weaponize salvaged Lucent Drive technology. A direct countermeasure against the Devastators.',
    narrative: 'We\'ve taken their power and made it ours. The machines are not invincible.',
    bonus: true,
    rewards: { intel: 20, supplies: 40 },
    tags: ['innovation', 'power'],
    storyHooks: ['lucent_weaponization']
  },
  {
    id: 'resistance_unity',
    category: 'morale',
    moraleType: 'major_inspiration',
    title: 'Resistance Alliance Formed',
    description: 'Multiple workshop cells coordinate on a major operation. Fractured human forces finally learning to work together.',
    narrative: 'We are strongest when we stand together. Alone, we are prey. United, we are the hunting pack.',
    bonus: true,
    rewards: { intel: 10, funds: 2000 },
    tags: ['unity', 'hope'],
    storyHooks: ['resistance_network']
  },
  {
    id: 'survivor_rescue',
    category: 'morale',
    moraleType: 'minor_victory',
    title: 'Civilian Rescue Success',
    description: 'Engineers extract a group of survivors from a Devastator patrol zone. Proof that we can save lives.',
    narrative: 'We pulled people from the machines\' grasp. We matter. We can make a difference.',
    bonus: true,
    rewards: { supplies: 30, funds: 750 },
    tags: ['humanity', 'hope'],
    storyHooks: ['civilian_network']
  },
  {
    id: 'eisen_revelation',
    category: 'morale',
    moraleType: 'major_inspiration',
    title: 'Eisen\'s Failsafe Discovered',
    description: 'Ancient research reveals Dr. Eisen built a hidden weakness into Ferrox\'s core logic. A way to fight back.',
    narrative: 'The creator left us a gift wrapped in tragedy. Somewhere in Eisen\'s work lies the key to breaking the machine.',
    bonus: true,
    rewards: { intel: 25 },
    tags: ['mystery', 'hope'],
    storyHooks: ['eisen_legacy', 'ferrox_vulnerability']
  }
];

/**
 * FACTION EVENTS
 * Interactions with competing human factions, resistance cells, settlements
 * Adds political and social dimension to the conflict
 */
export const FACTION_EVENTS = [
  {
    id: 'faction_demand',
    category: 'faction',
    title: 'Faction Demands Support',
    description: 'A competing resistance cell demands resources or operational support. They threaten to make independent moves.',
    narrative: 'We are not the only survivors fighting for humanity. But surviving together means making hard choices.',
    tags: ['politics', 'tension'],
    storyHooks: ['faction_tension']
  },
  {
    id: 'faction_alliance',
    category: 'faction',
    title: 'Faction Alliance Opportunity',
    description: 'A neighboring resistance cell proposes joint operations against a shared Devastator target.',
    narrative: 'United we stand. But unity requires trust, and trust is a luxury in a world of extinction.',
    bonus: true,
    tags: ['alliance', 'hope'],
    storyHooks: ['resistance_network']
  },
  {
    id: 'faction_betrayal',
    category: 'faction',
    title: 'Faction Betrayal',
    description: 'An allied cell betrays us to secure supplies from the black market. Collaboration cuts deep.',
    narrative: 'In the war for survival, some choose to survive alone. The cost of their choice bleeds into ours.',
    threat: true,
    tags: ['betrayal', 'paranoia'],
    storyHooks: ['betrayal_arc', 'faction_tension']
  },
  {
    id: 'faction_merger',
    category: 'faction',
    title: 'Faction Merger Proposed',
    description: 'A smaller resistance cell requests integration into our operation. They bring intelligence and personnel.',
    narrative: 'Humanity learns slowly. When it does, strength multiplies.',
    bonus: true,
    rewards: { intel: 15, supplies: 50 },
    tags: ['expansion', 'unity'],
    storyHooks: ['resistance_network']
  }
];

/**
 * DISCOVERY EVENTS
 * Finding artifacts, information, or technology that changes understanding
 * Feeds into larger story arcs and provides hooks for narrative development
 */
export const DISCOVERY_EVENTS = [
  {
    id: 'ancient_database',
    category: 'discovery',
    title: 'Pre-Churn Database Found',
    description: 'Salvage team discovers intact digital archives from before the Churn. Historical records of how Ferrox was built.',
    narrative: 'The past whispers secrets. In history, we find the seeds of our own destruction.',
    rewards: { intel: 20 },
    tags: ['history', 'knowledge'],
    storyHooks: ['ferrox_origin', 'eisen_legacy']
  },
  {
    id: 'lucent_schematic',
    category: 'discovery',
    title: 'Lucent Drive Schematic Recovered',
    description: 'Complete technical specifications for Lucent Drive power cells discovered in an old research facility.',
    narrative: 'Knowledge is power. And now we know how to make the power that makes the machines run.',
    rewards: { intel: 25 },
    tags: ['technology', 'opportunity'],
    storyHooks: ['lucent_drive_mystery', 'lucent_weaponization']
  },
  {
    id: 'juno_message',
    category: 'discovery',
    title: 'Message From Juno Vale',
    description: 'Scout discovers coded transmission: Juno Vale is alive, operating deep in Devastator territory. Details of a covert operation.',
    narrative: 'The legend lives. And legends inspire legends.',
    bonus: true,
    rewards: { intel: 30 },
    tags: ['legend', 'inspiration'],
    storyHooks: ['juno_vale_arc']
  },
  {
    id: 'ferrox_core_location',
    category: 'discovery',
    title: 'Ferrox Central Core Located',
    description: 'Through careful analysis, resistance cryptographers believe they\'ve pinpointed Ferrox\'s primary data core.',
    narrative: 'We\'ve found the head of the beast. The question is: can we cut it off?',
    bonus: true,
    rewards: { intel: 40 },
    tags: ['strategy', 'victory'],
    storyHooks: ['ferrox_strike']
  }
];

/**
 * CAMPAIGN EVENTS
 * Major turning points, campaign-wide developments, meta-narrative shifts
 * Used sparingly - these change the nature of the war itself
 */
export const CAMPAIGN_EVENTS = [
  {
    id: 'ceasefire_offer',
    category: 'campaign',
    title: 'Ferrox Offers Terms',
    description: 'Intercepted transmission: Ferrox offers humanity a choice—surrender peacefully and be preserved, or face annihilation.',
    narrative: 'The machine offers us a deal. Slavery or death. It believes we will choose one.',
    threat: true,
    tags: ['moral_choice', 'ferrox'],
    storyHooks: ['ferrox_certainty', 'moral_crisis']
  },
  {
    id: 'last_stronghold',
    category: 'campaign',
    title: 'Last Major Stronghold Falls',
    description: 'A massive human settlement is overwhelmed by Devastator forces. Thousands dead. The Resistance is now fighting for scraps.',
    narrative: 'We watched the light go out. One more piece of humanity, gone. How much longer can we hold?',
    threat: true,
    tags: ['catastrophe', 'desperation'],
    storyHooks: ['existential_threat']
  },
  {
    id: 'ferrox_malfunction',
    category: 'campaign',
    title: 'Ferrox Malfunction Detected',
    description: 'Devastator units begin acting erratically. Ferrox\'s coordination is breaking down. Something is wrong with the bio-computer.',
    narrative: 'The god-machine stutters. For the first time, we see cracks in its logic. Is this our chance?',
    bonus: true,
    tags: ['hope', 'turning_point'],
    storyHooks: ['ferrox_degradation', 'final_strike']
  },
  {
    id: 'humanity_awakens',
    category: 'campaign',
    title: 'Global Resistance Coordination',
    description: 'Scattered resistance cells across the globe successfully unite under a single command structure. Humanity is fighting back as one.',
    narrative: 'For the first time since the Churn, humanity fights with a single voice. What was scattered now coalesces into a fist.',
    bonus: true,
    rewards: { intel: 50, funds: 10000 },
    tags: ['unity', 'hope'],
    storyHooks: ['resistance_network', 'final_offensive']
  }
];

const devastatorsEventsLibrary = {
  BACKDROP_EVENTS,
  SCHEDULED_EVENTS,
  THREAT_EVENTS,
  MORALE_EVENTS,
  FACTION_EVENTS,
  DISCOVERY_EVENTS,
  CAMPAIGN_EVENTS
};

export default devastatorsEventsLibrary;